/**
 * ShareDashboardDialog Component
 * Dialog for sharing dashboards with other users
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { ScrollArea } from '../../ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { toast } from 'sonner';
import { cn } from '../../../lib/utils';
import {
  Users,
  Link2,
  Mail,
  Trash2,
  Copy,
  Check,
  Eye,
  Edit,
  Clock,
  ExternalLink,
  Plus,
  RefreshCw,
} from 'lucide-react';
import {
  getDashboardShares,
  shareDashboard,
  unshareDashboard,
  createShareLink,
  deleteShareLink,
} from '../../../lib/api';

export const ShareDashboardDialog = ({ open, onOpenChange, dashboard }) => {
  const { user } = useAuth();
  const [shares, setShares] = useState([]);
  const [links, setLinks] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newPermission, setNewPermission] = useState('view');
  const [copiedLinkId, setCopiedLinkId] = useState(null);

  // Fetch shares when dialog opens
  useEffect(() => {
    if (open && dashboard?.id && user?.email) {
      fetchShares();
    }
  }, [open, dashboard?.id, user?.email]);

  const fetchShares = async () => {
    setIsLoading(true);
    try {
      const response = await getDashboardShares(dashboard.id, user.email);
      setShares(response.data.shares || []);
      setLinks(response.data.links || []);
    } catch (error) {
      console.error('Failed to fetch shares:', error);
      toast.error('Failed to load sharing settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleShare = async () => {
    if (!newEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }

    try {
      await shareDashboard(dashboard.id, user.email, {
        email: newEmail.trim(),
        permission: newPermission,
      });
      toast.success(`Dashboard shared with ${newEmail}`);
      setNewEmail('');
      fetchShares();
    } catch (error) {
      console.error('Failed to share:', error);
      toast.error('Failed to share dashboard');
    }
  };

  const handleUnshare = async (email) => {
    try {
      await unshareDashboard(dashboard.id, user.email, email);
      toast.success('Access removed');
      fetchShares();
    } catch (error) {
      console.error('Failed to unshare:', error);
      toast.error('Failed to remove access');
    }
  };

  const handleCreateLink = async () => {
    try {
      const response = await createShareLink(dashboard.id, user.email, {
        expires_days: 30,
        permission: 'view',
      });
      toast.success('Share link created');
      fetchShares();
    } catch (error) {
      console.error('Failed to create link:', error);
      toast.error('Failed to create share link');
    }
  };

  const handleDeleteLink = async (linkId) => {
    try {
      await deleteShareLink(dashboard.id, user.email, linkId);
      toast.success('Link deleted');
      fetchShares();
    } catch (error) {
      console.error('Failed to delete link:', error);
      toast.error('Failed to delete link');
    }
  };

  const copyLinkToClipboard = (linkId) => {
    const url = `${window.location.origin}/shared/${linkId}`;
    navigator.clipboard.writeText(url);
    setCopiedLinkId(linkId);
    toast.success('Link copied to clipboard');
    setTimeout(() => setCopiedLinkId(null), 2000);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
            <Users className="w-5 h-5 text-primary" />
            Share Dashboard
          </DialogTitle>
          <DialogDescription>
            Share "{dashboard?.title || 'Dashboard'}" with others
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="people" className="mt-4">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="people" className="gap-2">
              <Mail className="w-4 h-4" />
              People
            </TabsTrigger>
            <TabsTrigger value="links" className="gap-2">
              <Link2 className="w-4 h-4" />
              Links
            </TabsTrigger>
          </TabsList>

          {/* Share with People */}
          <TabsContent value="people" className="space-y-4 mt-4">
            {/* Add new share */}
            <div className="space-y-2">
              <Label className="text-xs">Add people</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="Enter email address"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleShare()}
                  className="flex-1"
                />
                <Select value={newPermission} onValueChange={setNewPermission}>
                  <SelectTrigger className="w-[100px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="view">
                      <div className="flex items-center gap-2">
                        <Eye className="w-3 h-3" />
                        View
                      </div>
                    </SelectItem>
                    <SelectItem value="edit">
                      <div className="flex items-center gap-2">
                        <Edit className="w-3 h-3" />
                        Edit
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={handleShare} size="icon">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Shared with list */}
            <div className="space-y-2">
              <Label className="text-xs">Shared with</Label>
              <ScrollArea className="h-[200px] rounded-lg border border-border/50 p-2">
                {isLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <RefreshCw className="w-5 h-5 animate-spin text-muted-foreground" />
                  </div>
                ) : shares.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground">
                    <Users className="w-8 h-8 mb-2 opacity-50" />
                    <p className="text-sm">Not shared with anyone yet</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {shares.map((share) => (
                      <div
                        key={share.id || share.shared_with_email}
                        className="flex items-center justify-between p-2 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <span className="text-xs font-semibold text-primary">
                              {share.shared_with_email?.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">{share.shared_with_email}</p>
                            <div className="flex items-center gap-2">
                              <Badge variant="secondary" className="text-xs">
                                {share.permission === 'edit' ? (
                                  <><Edit className="w-3 h-3 mr-1" /> Can edit</>
                                ) : (
                                  <><Eye className="w-3 h-3 mr-1" /> Can view</>
                                )}
                              </Badge>
                            </div>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => handleUnshare(share.shared_with_email)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>
          </TabsContent>

          {/* Share Links */}
          <TabsContent value="links" className="space-y-4 mt-4">
            {/* Create new link */}
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm font-medium">Share links</p>
                <p className="text-xs text-muted-foreground">Anyone with the link can view</p>
              </div>
              <Button onClick={handleCreateLink} size="sm" className="gap-2">
                <Plus className="w-4 h-4" />
                Create Link
              </Button>
            </div>

            {/* Links list */}
            <ScrollArea className="h-[200px] rounded-lg border border-border/50 p-2">
              {isLoading ? (
                <div className="flex items-center justify-center h-full">
                  <RefreshCw className="w-5 h-5 animate-spin text-muted-foreground" />
                </div>
              ) : links.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground">
                  <Link2 className="w-8 h-8 mb-2 opacity-50" />
                  <p className="text-sm">No share links created yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {links.map((link) => (
                    <div
                      key={link.link_id}
                      className="p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Link2 className="w-4 h-4 text-primary" />
                          <code className="text-xs bg-muted px-2 py-0.5 rounded">
                            .../{link.link_id}
                          </code>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => copyLinkToClipboard(link.link_id)}
                          >
                            {copiedLinkId === link.link_id ? (
                              <Check className="w-4 h-4 text-green-500" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-destructive hover:text-destructive"
                            onClick={() => handleDeleteLink(link.link_id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Eye className="w-3 h-3" />
                          {link.views || 0} views
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Expires {formatDate(link.expires_at)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default ShareDashboardDialog;
